import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { useSelector, useDispatch } from "react-redux";
import Modal, { ModalActions, ModalContent, ModalHeader, ModalTitle } from "../components/Modal";
import { Button } from "../components/Button";
import Select from 'react-select'
import Ai, {
  selectAllQuotas, selectAllVMs, selectNotebookTemplate, selectCommunityVMs
} from '../store/models/Ai';
import {
  getVMOptions, formatPrice, getErrorMsg, generateRandomIdWithPrefix,
  checkIsBasicPlan, getCommunityVMOptions, roundUpTo2Decimals
} from '../utils';
import { selectCurrentProjectId } from '../store/models/Project';
import { ModalTypes } from '../constants';
import { selectCurrentUser, selectCurrentUserId } from '../store/models/User';
import UIState from '../store/UIState';
import { toast } from "react-toastify";
import Organization, { selectCurrentOrgId } from '../store/models/Organization';
import Tooltip from '../components/Tooltip';
import { QuestionMarkIcon } from '../components/Svg';
import CustomSelect from '../components/CustomSelect';
import CustomOption from '../components/CustomOption';
import Loader from '../components/Loader';
import { Accordion, AccordionHeader } from '../components/Accordion';



const selectStyles = {
  container: (styles, { isDisabled }) => ({
    ...styles,
    flex: 1,
    height: 40,
    cursor: isDisabled ? 'not-allowed' : 'default',
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    paddingLeft: 15,
    backgroundColor: '#191D29',
    borderColor: '#3D4463',
    borderRadius: 6,
    ':hover': {
      borderColor: 'white',
    },
    '.selected': {
      borderColor: 'white',
    },
  }),
  menu: (styles, state) => ({
    ...styles,
    border: '1px solid #3D4463',
    backgroundColor: '#191D29',
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: '#191D29',
      borderColor: '#3D4463',
      color: isDisabled ? '#636B91' : (isFocused ? 'white' : (isSelected ? 'white' : '#636B91')),
      opacity: isDisabled ? 0.5 : 1,
    };
  },
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: '#8A8FB5',
  }),
  indicatorsContainer: (styles, { isDisabled }) => ({
    ...styles,
    display: isDisabled ? 'none' : 'flex',
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    color: '#636B91',
    backgroundColor: '#636B91',
    display: 'none'
  }),
  input: styles => ({ ...styles }),
  placeholder: styles => ({ ...styles, color: '#636B91' }),
  singleValue: (styles, { data }) => ({ ...styles, color: '#636B91', fontWeight: '500' }),
};

export const CreateNotebookModal = ({ onRequestClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const userId = useSelector(state => selectCurrentUserId(state));
  const curUser = useSelector(state => selectCurrentUser(state));
  const orgId = useSelector(state => selectCurrentOrgId(state));
  const projectId = useSelector(state => selectCurrentProjectId(state));
  const notebookTemplate = useSelector(state => selectNotebookTemplate(state));
  const VMs = useSelector(state => selectAllVMs(state));
  const quotas = useSelector(state => selectAllQuotas(state));
  const [imagesOptions, setImagesOptions] = useState(notebookTemplate
    ? notebookTemplate.container_images.map(img => ({ label: img, value: img })) : []);

  let [suggestedVM, setSuggestedVM] = useState(_.get(notebookTemplate, 'suggested_vms.0'));
  let [vmOptions, setVmOptions] = useState(getVMOptions(VMs, suggestedVM));
  let defaultVM = vmOptions.filter(o => o.value === suggestedVM)[0] || vmOptions[0];
  let [price, setPrice] = useState(formatPrice(VMs[suggestedVM || vmOptions[0].value].price_hour / 100));
  const defaultName = generateRandomIdWithPrefix('notebook', 20);
  let communityVms = useSelector(state => selectCommunityVMs(state));
  const communityVmOptions = getCommunityVMOptions(communityVms);

  if (!vmOptions) {
    return <Loader />
  }

  const nameRef = useRef();
  const dnameRef = useRef();
  const imageRef = useRef();
  const portRef = useRef();
  const vmRef = useRef();
  const pwdRef = useRef();
  const authUserRef = useRef();
  const authPwdRef = useRef();

  const [enablePwd, setEnablePwd] = useState(false);
  const [enableAuth, setEnableAuth] = useState(false);
  const [isThetaNode, setIsThetaNode] = useState(true);
  const [cVmLoading, setCVmLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [vmOption, setVmOption] = useState(defaultVM);
  const [maxHr, setMaxHr] = useState()

  useEffect(() => {
    (async () => {
      try {
        setCVmLoading(true);
        await dispatch(Ai.actions.fetchCommunityVMs(projectId));
      } catch (e) {

      } finally {
        setCVmLoading(false);
      }
    })()
  }, [projectId])
  
  useEffect(() => {
      onVMChange(defaultVM, 'theta');
    }, [])

  const onSubmit = async () => {
    if (_.isNil(userId)) {
      dispatch(UIState.actions.showModal(ModalTypes.SIGNUP))
      return;
    }
    let name = nameRef.current.value;
    let image = imageRef.current.state.selectValue[0].value;
    let port = portRef.current.value;
    let vm = vmRef.current.state.selectValue[0].value;
    let pwd = enablePwd ? pwdRef.current.value : "";
    let dname = dnameRef.current.value;
    let authUser = enableAuth ? authUserRef.current.value : "";
    let authPwd = enableAuth ? authPwdRef.current.value : "";
    // console.log('name:', name, ' image:', image)
    // console.log('port:', port, ' vm:', vm)
    // console.log('projectId:', projectId)
    // console.log('pwd:', pwd)
    const imageId = notebookTemplate.id;
    const tags = notebookTemplate.tags;
    if (name.length) {
      if (!/^[A-Za-z0-9]+$/.test(name)) {
        const errorStr = 'Invalid ID. The ID should only contain letters and numbers.'
        setError(errorStr);
        toast.error('Error: ' + errorStr);
        return;
      }
      if (name.length > 20) {
        const errorStr = 'Invalid ID. The length of the ID should be less than or equal to 20.'
        setError(errorStr);
        toast.error('Error: ' + errorStr)
        return;
      }
    }
    let maxHrRate = ''
    if (maxHr != undefined) {
      maxHrRate = maxHr;
      if (maxHrRate < _.get(price, 'unit')) {
        setError(`Max hourly rate can't be lower than current rate: $${price}.`);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const deployment = await dispatch(Ai.actions.createDeployment({
        name: name,
        project_id: projectId,
        deployment_image_id: imageId,
        container_image: image,
        min_replicas: 1,
        max_replicas: 1,
        vm_id: vm,
        password: pwd,
        annotations: {
          tags: JSON.stringify(tags),
          nickname: dname
        },
        auth_username: authUser,
        auth_password: authPwd,
        price_hr: Math.ceil(maxHrRate) * 10000 + ''
      }))
      await dispatch(Ai.actions.fetchJupyterNotebooks(projectId));
      onRequestClose();
    } catch (e) {
      let quotaId;
      try {
        quotaId = await dispatch(Organization.actions.fetchOrgQuotaId(orgId))
      } catch (e) {
        console.log('error in fetch quotaId:', e.message);
      }
      let error = getErrorMsg(e.message, onRequestClose, curUser, orgId, vm, checkIsBasicPlan(quotas, quotaId));
      setLoading(false);
      setError(error);
      if (error === e.message) {
        toast.error(e.message)
      }
    }
  }

  const onVMChange = async (v, type) => {
    if (!v) return;
    if (type === 'theta') {
      setPrice(formatPrice(VMs[v.value].price_hour / 100));
    } else {
      setPrice(roundUpTo2Decimals(communityVms[v.value].label_onthetaedgecloud_com_price_hr / 10000));
    }
    try {
      setImageLoading(true);
      let params = {};
      switch (type) {
        case 'theta':
          params.machine_type = v.value;
          break;
        case 'community':
          const vm = communityVms[v.value];
          params = {
            cuda_major: vm.label_nvidia_com_cuda_runtime_major,
            cuda_minor: vm.label_nvidia_com_cuda_runtime_minor,
            driver_major: vm.label_nvidia_com_cuda_driver_major,
            driver_minor: vm.label_nvidia_com_cuda_driver_minor,
            gpu_count: vm.label_nvidia_com_gpu_count,
            gpu_memory: vm.label_nvidia_com_gpu_memory,
            gpu_product: vm.label_nvidia_com_gpu_product
          }
          break;
        default:
          break;
      }
      let res = await dispatch(Ai.actions.fetchImagesByVMs(notebookTemplate.id, params));
      setImagesOptions(res.map(o => ({
        label: o.container_image_uri,
        value: o.container_image_uri,
        is_compatible: o.is_compatible
      })))
      setImageLoading(false);
    } catch (e) {
      setImageLoading(false);
      console.log('error:', e.message)
    }
    setVmOption(v);
  }

  const onVMTypeChange = (v) => {
    setIsThetaNode(v => !v)

    let newVmOption = isThetaNode ? (communityVmOptions[0] || null) : defaultVM;
    setVmOption(newVmOption)
    onVMChange(newVmOption, isThetaNode ? 'community' : 'theta');

    if (!isThetaNode) {
      setPrice(VMs[defaultVM.value].price_hour / 100);
    } else {
      if (communityVmOptions[0]) {
        setPrice(roundUpTo2Decimals(communityVms[communityVmOptions[0].value]
          .label_onthetaedgecloud_com_price_hr / 10000));
        return;
      }
      setPrice(0)
    }
  }

  const onImageChange = (v) => {
    const index = notebookTemplate.container_images.findIndex(img => img === v.value);
    let newSuggestedVM = _.get(notebookTemplate, `suggested_vms.${index}`)
    let newOptions = getVMOptions(VMs, newSuggestedVM)

    setVmOptions(newOptions);
    setSuggestedVM(newSuggestedVM);
    if (!isThetaNode) return;
    setVmOption(newOptions.filter(o => o.value === newSuggestedVM)[0] || newOptions[0]);
    setPrice(formatPrice(VMs[newSuggestedVM || newOptions[0].value].price_hour / 100));
  }

  useEffect(() => {
    if (price) {
      setMaxHr(price)
    }
  }, [price])

  const handleMaxHrChange = (e) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setMaxHr(value);
    }
  }

  return (
    <Modal className={cx("CreateNotebookModal")}
      onRequestClose={onRequestClose}>

      <ModalHeader>
        <ModalTitle align={'row'}>
          Create New Notebook
          <Tooltip tooltip={'Open documentation'}>
            <a href="https://docs.thetatoken.org/docs/ai-prototyping-with-jupyter-notebook" target='_blank'>
              <QuestionMarkIcon />
            </a>
          </Tooltip>
        </ModalTitle>
      </ModalHeader>

      <ModalContent>
        <div className='CreateNotebookModal__row'>
          <div className='CreateNotebookModal__select-wrap name'>
            <div className='CreateNotebookModal__select-label'>Notebook ID</div>
            <input className={"CreateNotebookModal__input"}
              placeholder={'Enter new notebook ID'}
              ref={nameRef}
              defaultValue={defaultName}
              disabled={true}
            />
          </div>
          <div className='CreateNotebookModal__select-wrap name'>
            <div className='CreateNotebookModal__select-label'>Notebook Name&nbsp;<div className='text-optional'>(optional)</div></div>
            <input className={"CreateNotebookModal__input"}
              placeholder={'Enter new notebook name'}
              ref={dnameRef}
            />
          </div>
        </div>
        <div className='CreateNotebookModal__row'>
          <div className='CreateNotebookModal__select-wrap image'>
            <div className='CreateNotebookModal__select-label'>Notebook Image</div>
            {imageLoading ? <Loader /> : <Select options={imagesOptions}
              className={"CreateNotebookModal__selector"}
              defaultValue={imagesOptions[0]}
              styles={selectStyles}
              placeholder={'Select Notebook Image'}
              isDisabled={imagesOptions.length < 2}
              onChange={onImageChange}
              ref={imageRef}
            />}
          </div>
        </div>
        <div className='CreateNotebookModal__row'>
          <div className='CreateNotebookModal__select-wrap port'>
            <div className='CreateNotebookModal__select-label'>Container Port</div>
            <input className={"CreateNotebookModal__input"}
              placeholder={'Enter new notebook container port'}
              defaultValue={notebookTemplate.container_port}
              ref={portRef}
            />
          </div>
          <div className='CreateNotebookModal__select-wrap pwd'>
            <div className='CreateNotebookModal__select-label'>Notebook password</div>
            <div className='CreateNotebookModal__select-content'>
              <label className='CreateNotebookModal__switch'>
                <input className='CreateNotebookModal__switch-input' type="checkbox" value={enablePwd} onClick={() => setEnablePwd(v => !v)} />
                <span className="CreateNotebookModal__switch-slider"></span>
              </label>
              <input className={"CreateNotebookModal__input pwd"}
                placeholder={enablePwd ? 'Enter notebook password' : 'Default password is empty'}
                disabled={!enablePwd}
                ref={pwdRef}
                type='password'
              />
            </div>
          </div>
        </div>
        <div className='CreateNotebookModal__row'>
          <div className='CreateNotebookModal__select-wrap switch'>
            <div className='CreateNotebookModal__select-label'>
              {'Community nodes'}
            </div>
            <div className='CreateNotebookModal__select-content switch'>
              <label className='CreateNotebookModal__switch'>
                <input className='CreateNotebookModal__switch-input'
                  type="checkbox"
                  value={isThetaNode}
                  onClick={onVMTypeChange}
                />
                <span className="CreateNotebookModal__switch-slider"></span>
              </label>
            </div>
          </div>
          <div className='CreateNotebookModal__select-wrap vms'>
            <div className='CreateNotebookModal__select-label'>Machine type</div>
            {isThetaNode ? <CustomSelect options={vmOptions}
              className={"CreateNotebookModal__selector"}
              value={vmOption}
              styles={selectStyles}
              placeholder={'Select Machine type'}
              isDisabled={vmOptions.length < 2}
              selectRef={vmRef}
              onChange={(v) => onVMChange(v, 'theta')}
              isOptionDisabled={(option) => {
                if (!suggestedVM) return false;
                return option.price < Number(VMs[suggestedVM].price_hour)
              }} /> : (cVmLoading ? <Loader /> : <CustomSelect options={communityVmOptions}
                className={"CreateNotebookModal__selector"}
                value={vmOption}
                styles={selectStyles}
                placeholder={'Select Machine type'}
                // isDisabled={communityVmOptions.length < 2}
                selectRef={vmRef}
                onChange={(v) => onVMChange(v, 'community')}
                customComponents={{ Option: CustomOption }}
                noOptionsMessage={() => "No community node available"}
              />)}
          </div>
        </div>
        {/* <div className='CreateNotebookModal__row'>
          <div className='CreateNotebookModal__select-wrap vms'>
            <div className='CreateNotebookModal__select-label'>Machine type</div>
            <CustomSelect options={vmOptions}
              className={"CreateNotebookModal__selector"}
              value={selectedVMOption}
              styles={selectStyles}
              placeholder={'Select Machine type'}
              isDisabled={vmOptions.length < 2}
              onChange={onVMChange}
              selectRef={vmRef}
              isOptionDisabled={(option) => {
                return option.price < Number(VMs[suggestedVM || vmOptions[0].value].price_hour)
              }}
            />
          </div>
        </div> */}
        {!isThetaNode && <Accordion
          header={<AccordionHeader
            title="Advanced Options"
          />}
          body={<>
            <div className='CreateDeploymentModal__row'>
              {!isThetaNode && <div className='CreateDeploymentModal__section-wrap'>
                <div className='CreateDeploymentModal__select-label'>Max Hourly Rate ($)</div>
                <div className='CreateDeploymentModal__row-wrap'>
                  <input className={"CreateDeploymentModal__input"}
                    placeholder={'Set Max Hourly Rate'}
                    type="number"
                    value={maxHr}
                    onChange={handleMaxHrChange}
                    step={0.01}
                  />
                </div>
              </div>}
              <div className='CreateDeploymentModal__section-wrap'></div>
            </div>
          </>} />}
        <div className='CreateNotebookModal__price-info'>
          <div className='CreateNotebookModal__price-info--note'>
            Cost Estimation
          </div>
          <div className='CreateNotebookModal__price-info--price'>
            ~ ${price} per hour.
          </div>
        </div>
        {error && <div className={cx("CreateNotebookModal__error")}>{error}</div>}
      </ModalContent>

      <ModalActions>
        <Button color={"green"} onClick={onSubmit} title={"Create New Notebook"} disabled={imageLoading} loading={loading} />
        <Button color={"transparent"} onClick={onRequestClose} title={"Cancel"} />
      </ModalActions>

    </Modal>
  );
}